document.addEventListener("DOMContentLoaded", () => {
    const OWNED_IDS_STORAGE_KEY = 'ovaniOwnedProductIds';

    // Helper function to decode HTML entities
    function decodeHtmlEntities(text) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");
        return doc.documentElement.textContent;
    }

    function readCachedOwnedIds() {
        try {
            const raw = sessionStorage.getItem(OWNED_IDS_STORAGE_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return [];
            return parsed.map((id) => String(id).trim()).filter(Boolean);
        } catch (error) {
            console.error('Error reading cached owned product IDs:', error);
            return [];
        }
    }

    function writeCachedOwnedIds(ids) {
        try {
            sessionStorage.setItem(OWNED_IDS_STORAGE_KEY, JSON.stringify(Array.from(ids)));
        } catch (error) {
            console.error('Error writing cached owned product IDs:', error);
        }
    }

    /**
     * Merge page payload with sessionStorage so collection ?page=2
     * (which can empty the Liquid paginate) does not wipe ownership.
     */
    function getOwnedProductsData() {
        const el = document.getElementById('ovani-owned-products');
        let pageIds = [];
        let titles = [];
        let ordersCount = 0;

        if (el) {
            try {
                const data = JSON.parse(el.textContent || '{}');
                pageIds = (Array.isArray(data.ids) ? data.ids : [])
                    .map((id) => String(id).trim())
                    .filter(Boolean);
                titles = (Array.isArray(data.titles) ? data.titles : [])
                    .map((title) => String(title || '').trim())
                    .filter(Boolean);
                ordersCount = Number(data.orders_count) || 0;
            } catch (error) {
                console.error('Error parsing owned products data:', error);
            }
        }

        const cachedIds = readCachedOwnedIds();
        const merged = new Set([...cachedIds, ...pageIds]);

        // If this page returned fewer IDs than we already know (e.g. collection page 2
        // shifted customer.orders pagination), keep the union — do not shrink.
        if (pageIds.length > 0 || cachedIds.length === 0) {
            writeCachedOwnedIds(merged);
        } else if (ordersCount > 50 && cachedIds.length > pageIds.length) {
            writeCachedOwnedIds(merged);
        }

        return { ids: merged, titles, ordersCount };
    }

    function parseIdList(value) {
        if (!value) return [];
        return String(value)
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean);
    }

    /**
     * Ownership rules:
     * - Green: this exact variant is owned, OR every bundle-content ID owned,
     *   OR product ID owned when there are no bundle contents (single pack)
     * - Yellow: some (not all) bundle-content IDs owned
     * - Do not green a bundle just because its product ID is owned
     */
    function getOwnershipState(productId, variantId, bundleContentIds, ownedIds) {
        const variant = variantId ? String(variantId).trim() : '';
        if (variant && ownedIds.has(variant)) {
            return 'owned';
        }

        if (bundleContentIds.length) {
            let ownedCount = 0;
            for (const childId of bundleContentIds) {
                if (ownedIds.has(childId)) ownedCount += 1;
            }

            if (ownedCount === 0) return 'none';
            if (ownedCount === bundleContentIds.length) return 'owned';
            return 'partial';
        }

        const id = productId ? String(productId).trim() : '';
        if (id && ownedIds.has(id)) {
            return 'owned';
        }

        return 'none';
    }

    function applyCardOwnership(ownedIds) {
        const cards = document.querySelectorAll('.product-card-wrapper');
        cards.forEach((card) => {
            const productId = card.dataset.productId || '';
            const variantId = card.dataset.variantId || '';
            const bundleContentIds = parseIdList(card.dataset.bundleContentIds);
            const state = getOwnershipState(productId, variantId, bundleContentIds, ownedIds);

            const ownedCheckmark = card.querySelector('.product-owned-checkmark');
            const partialCheckmark = card.querySelector('.product-partially-owned-checkmark');

            if (ownedCheckmark) ownedCheckmark.style.display = 'none';
            if (partialCheckmark) partialCheckmark.style.display = 'none';

            if (state === 'owned' && ownedCheckmark) {
                ownedCheckmark.style.display = 'block';
            } else if (state === 'partial' && partialCheckmark) {
                partialCheckmark.style.display = 'block';
            }
        });
    }

    function applyPdpOwnership(ownedIds) {
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return;

        const productId = productInfo.dataset.productId || '';
        const variantId = productInfo.dataset.variantId || productInfo.dataset.id || '';
        const bundleContentIds = parseIdList(productInfo.dataset.bundleContentIds);
        const state = getOwnershipState(productId, variantId, bundleContentIds, ownedIds);

        const productReminder = document.querySelector('#product-template__download-reminder');
        const productForm = document.querySelector('#form-container');
        const form = productForm ? productForm.querySelector('form') : null;
        const downloadCTA = productForm ? productForm.querySelector('.download-library-link') : null;
        const partiallyOwned = document.querySelector('#product-template__partially_owned');

        if (form) form.style.display = '';
        if (productReminder) productReminder.style.display = '';
        if (downloadCTA) downloadCTA.style.display = 'none';
        if (partiallyOwned) partiallyOwned.style.display = 'none';

        if (state === 'owned') {
            if (form) form.style.display = 'none';
            if (productReminder) productReminder.style.display = 'none';
            if (downloadCTA) downloadCTA.style.display = 'block';
        } else if (state === 'partial') {
            if (partiallyOwned) partiallyOwned.style.display = 'block';
        }
    }

    function applyStorefrontOwnership() {
        const { ids: ownedIds } = getOwnedProductsData();
        applyPdpOwnership(ownedIds);
        applyCardOwnership(ownedIds);
    }

    window.applyStorefrontOwnership = applyStorefrontOwnership;

    if (window.location.href.includes('/apps/digital-downloads/')) {
        // Page-local Fileflare cart cleanup (separate from storefront ownership)
        fetch('/cart.js')
            .then((response) => response.json())
            .then((cart) => {
                const h3Elements = document.querySelectorAll('h3.dda-order__item-name');
                h3Elements.forEach((h3) => {
                    const productName = decodeHtmlEntities(h3.textContent.trim());
                    if (!productName) return;

                    const matchingItem = cart.items.find(
                        (item) =>
                            decodeHtmlEntities(item.title.trim()).toLowerCase() ===
                            productName.toLowerCase()
                    );

                    if (matchingItem) {
                        if (
                            confirm(
                                `You already own product "${productName}"! Remove it from the cart?`
                            )
                        ) {
                            fetch('/cart/change.js', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                    id: matchingItem.key,
                                    quantity: 0,
                                }),
                            })
                                .then((response) => {
                                    if (!response.ok) {
                                        throw new Error(`Error: ${response.statusText}`);
                                    }
                                    return response.json();
                                })
                                .then(() => {
                                    alert(`"${productName}" has been removed from your cart.`);
                                    window.location.reload();
                                })
                                .catch((error) => {
                                    console.error('Error removing item from cart:', error);
                                    alert(
                                        'There was an issue removing the product from your cart. Please try again.'
                                    );
                                });
                        }
                    }
                });
            })
            .catch((error) => {
                console.error('Error fetching cart:', error);
            });
    } else {
        applyStorefrontOwnership();
    }
});
